import { useEffect, useRef, type RefObject } from 'react'

/** Overlays currently open. A history entry is popped only when the last one closes. */
let openCount = 0

/**
 * The control most recently pressed. Safari does not focus a button when it is clicked or
 * tapped, so `document.activeElement` at open time is just <body>, and focus had nowhere to
 * return to on close. Recording the press covers that.
 */
let lastPressed: HTMLElement | null = null
if (typeof document !== 'undefined') {
  document.addEventListener('pointerdown', (e) => {
    lastPressed = (e.target as Element | null)?.closest<HTMLElement>('button, a, [tabindex]') ?? null
  }, true)
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Everything a modal overlay (the nav drawer, a filter sheet) owes the user while it is open:
 *
 *  - Escape closes it.
 *  - The page behind does not scroll. On iOS a scroll that reaches the end of the sheet
 *    otherwise chains into the page, which then jumps when the sheet closes.
 *  - Focus moves into it, Tab cycles inside it, and focus returns to whatever opened it —
 *    so a keyboard or screen-reader user is never stranded behind the backdrop.
 *  - The Android back gesture closes it instead of leaving the page. A history entry is
 *    pushed on open and consumed on close, so back does exactly one thing.
 */
export function useOverlay(open: boolean, onClose: () => void, panel: RefObject<HTMLElement | null>) {
  // The latest onClose, without re-running the effect (and re-pushing history) every render.
  const close = useRef(onClose)
  close.current = onClose

  useEffect(() => {
    if (!open) return
    const active = document.activeElement as HTMLElement | null
    const opener = active && active !== document.body ? active : lastPressed

    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    // Focus the first control after paint, so the enter animation is not fighting a scroll.
    const raf = requestAnimationFrame(() => {
      const first = panel.current?.querySelector<HTMLElement>('[data-autofocus]')
        ?? panel.current?.querySelector<HTMLElement>(FOCUSABLE)
      first?.focus({ preventScroll: true })
    })

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); close.current(); return }
      if (e.key !== 'Tab' || !panel.current) return
      const items = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)]
        .filter((el) => el.offsetParent !== null)
      if (items.length === 0) return
      const first = items[0]!
      const last = items[items.length - 1]!
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
    }
    window.addEventListener('keydown', onKey)

    // Back closes the overlay. `closedByBack` stops the cleanup from popping a second time.
    let closedByBack = false
    openCount++
    if (!window.history.state?.overlay) window.history.pushState({ overlay: true }, '')
    const onPop = () => { closedByBack = true; close.current() }
    window.addEventListener('popstate', onPop)

    return () => {
      cancelAnimationFrame(raf)
      document.body.style.overflow = prevOverflow
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('popstate', onPop)
      // Closed some other way (✕, backdrop): drop the entry we pushed, but only if it is still
      // the current one — a navigation that replaced it has already moved past it. Deferred,
      // so React StrictMode's mount → unmount → mount in development (and an overlay that
      // closes as another opens) re-uses the entry instead of racing a back() against it.
      openCount--
      if (!closedByBack) {
        setTimeout(() => {
          if (openCount === 0 && window.history.state?.overlay) window.history.back()
        }, 0)
      }
      opener?.focus?.({ preventScroll: true })
    }
  }, [open, panel])
}
